import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  Timestamp,
  limit,
  writeBatch,
  increment
} from 'firebase/firestore';

/**
 * Service de gestion de la messagerie
 */
class MessagingService {
  constructor(db) {
    this.db = db;
  }

  /**
   * Créer une nouvelle conversation
   * @param {string[]} participants - IDs des participants
   * @param {'internal'|'client'|'group'} type - Type de conversation
   * @param {string} [clientId] - ID du client (pour les conversations client)
   * @returns {Promise<string>} ID de la conversation créée
   */
  async createConversation(participants, type, clientId = null) {
    try {
      const conversationData = {
        participants,
        type,
        lastMessage: '',
        lastMessageAt: Timestamp.now(),
        unreadCount: participants.reduce((acc, participant) => {
          acc[participant] = 0;
          return acc;
        }, {}),
        lastReadTimestamp: participants.reduce((acc, participant) => {
          acc[participant] = Timestamp.now();
          return acc;
        }, {}),
        archived: false
      };

      if (clientId) {
        conversationData.clientId = clientId;
      }

      const docRef = await addDoc(collection(this.db, 'conversations'), conversationData);
      return docRef.id;
    } catch (error) {
      console.error('Erreur création conversation:', error);
      throw error;
    }
  }

  /**
   * Créer une conversation de groupe
   * @param {string} groupName - Nom du groupe
   * @param {string} groupDescription - Description du groupe
   * @param {string[]} participants - IDs des participants
   * @param {string} createdBy - ID du créateur
   * @param {string} creatorName - Nom complet du créateur
   * @returns {Promise<string>} ID de la conversation groupe créée
   */
  async createGroupConversation(groupName, groupDescription, participants, createdBy, creatorName) {
    try {
      const conversationData = {
        type: 'group',
        groupName,
        groupDescription: groupDescription || '',
        participants,
        createdBy,
        createdAt: Timestamp.now(),
        lastMessage: '',
        lastMessageAt: Timestamp.now(),
        unreadCount: participants.reduce((acc, participant) => {
          acc[participant] = 0;
          return acc;
        }, {}),
        lastReadTimestamp: participants.reduce((acc, participant) => {
          acc[participant] = Timestamp.now();
          return acc;
        }, {}),
        archived: false
      };

      const docRef = await addDoc(collection(this.db, 'conversations'), conversationData);

      // Envoyer un message système pour indiquer la création du groupe
      await this.sendSystemMessage(
        docRef.id,
        `Groupe "${groupName}" créé par ${creatorName}`
      );

      return docRef.id;
    } catch (error) {
      console.error('Erreur création groupe:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour les informations d'un groupe
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur qui modifie
   * @param {Object} updates - Mises à jour (groupName, groupDescription)
   * @returns {Promise<void>}
   */
  async updateGroupInfo(conversationId, userId, updates) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation non trouvée');
      }

      const conversation = conversationDoc.data();

      // Vérifier que l'utilisateur est participant du groupe
      if (!conversation.participants.includes(userId)) {
        throw new Error('Vous n\'êtes pas autorisé à modifier ce groupe');
      }

      const updateData = {};

      if (updates.groupName !== undefined) {
        updateData.groupName = updates.groupName;
      }
      if (updates.groupDescription !== undefined) {
        updateData.groupDescription = updates.groupDescription;
      }

      await updateDoc(conversationRef, updateData);

      // Message système pour indiquer la modification
      if (updates.groupName) {
        await this.sendSystemMessage(
          conversationId,
          `Le nom du groupe a été modifié : "${updates.groupName}"`
        );
      }
    } catch (error) {
      console.error('Erreur mise à jour groupe:', error);
      throw error;
    }
  }

  /**
   * Ajouter un participant à un groupe
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur à ajouter
   * @param {string} addedBy - ID de celui qui ajoute
   * @param {string} userName - Nom de l'utilisateur ajouté
   * @returns {Promise<void>}
   */
  async addParticipantToGroup(conversationId, userId, addedBy, userName) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation non trouvée');
      }

      const conversation = conversationDoc.data();

      if (conversation.type !== 'group') {
        throw new Error('Cette conversation n\'est pas un groupe');
      }

      // Vérifier que celui qui ajoute est bien participant du groupe
      if (!conversation.participants.includes(addedBy)) {
        throw new Error('Vous n\'êtes pas autorisé à ajouter des participants à ce groupe');
      }

      if (conversation.participants.includes(userId)) {
        throw new Error('Cet utilisateur est déjà dans le groupe');
      }

      // Ajouter le participant
      const updatedParticipants = [...conversation.participants, userId];

      await updateDoc(conversationRef, {
        participants: updatedParticipants,
        [`unreadCount.${userId}`]: 0
      });

      // Message système
      await this.sendSystemMessage(
        conversationId,
        `${userName} a été ajouté au groupe`
      );
    } catch (error) {
      console.error('Erreur ajout participant:', error);
      throw error;
    }
  }

  /**
   * Retirer un participant d'un groupe
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur à retirer
   * @param {string} removedBy - ID de celui qui retire (doit être participant)
   * @param {string} userName - Nom de l'utilisateur retiré
   * @returns {Promise<void>}
   */
  async removeParticipantFromGroup(conversationId, userId, removedBy, userName) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation non trouvée');
      }

      const conversation = conversationDoc.data();

      if (conversation.type !== 'group') {
        throw new Error('Cette conversation n\'est pas un groupe');
      }

      // Vérifier que celui qui retire est participant (ou que l'utilisateur se retire lui-même)
      if (!conversation.participants.includes(removedBy) && removedBy !== userId) {
        throw new Error('Vous n\'êtes pas autorisé à retirer des participants de ce groupe');
      }

      if (!conversation.participants.includes(userId)) {
        throw new Error('Cet utilisateur n\'est pas dans le groupe');
      }

      // Retirer le participant
      const updatedParticipants = conversation.participants.filter(id => id !== userId);

      if (updatedParticipants.length < 2) {
        throw new Error('Un groupe doit avoir au moins 2 participants');
      }

      // Supprimer l'unread count pour cet utilisateur
      const batch = writeBatch(this.db);
      batch.update(conversationRef, {
        participants: updatedParticipants,
        [`unreadCount.${userId}`]: null
      });

      await batch.commit();

      // Message système
      await this.sendSystemMessage(
        conversationId,
        `${userName} a quitté le groupe`
      );
    } catch (error) {
      console.error('Erreur retrait participant:', error);
      throw error;
    }
  }

  /**
   * Quitter un groupe
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur qui quitte
   * @param {string} userName - Nom de l'utilisateur
   * @returns {Promise<void>}
   */
  async leaveGroup(conversationId, userId, userName) {
    return this.removeParticipantFromGroup(conversationId, userId, userId, userName);
  }

  /**
   * Envoyer un message système
   * @param {string} conversationId - ID de la conversation
   * @param {string} content - Contenu du message système
   * @returns {Promise<string>} ID du message
   */
  async sendSystemMessage(conversationId, content) {
    try {
      const messageData = {
        conversationId,
        senderId: 'system',
        senderName: 'Système',
        content,
        timestamp: Timestamp.now(),
        type: 'system'
      };

      const messageRef = await addDoc(collection(this.db, 'internalMessages'), messageData);

      // Mettre à jour la conversation
      const conversationRef = doc(this.db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        lastMessage: content,
        lastMessageAt: Timestamp.now()
      });

      return messageRef.id;
    } catch (error) {
      console.error('Erreur envoi message système:', error);
      throw error;
    }
  }

  /**
   * Envoyer un message
   * @param {string} conversationId - ID de la conversation
   * @param {string} senderId - ID de l'expéditeur
   * @param {string} senderName - Nom de l'expéditeur
   * @param {string} content - Contenu du message
   * @returns {Promise<string>} ID du message envoyé
   */
  async sendMessage(conversationId, senderId, senderName, content) {
    try {
      console.log('[MessagingService] 📝 Envoi message:', { conversationId, senderId, senderName });

      // Vérifier que la conversation existe et que l'utilisateur est participant
      const conversationRef = doc(this.db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error('Conversation non trouvée');
      }

      const conversation = conversationDoc.data();

      // Vérifier que l'expéditeur est bien participant de la conversation
      if (!conversation.participants.includes(senderId)) {
        throw new Error('Vous n\'êtes pas autorisé à envoyer des messages dans cette conversation');
      }

      // Créer le message
      const messageData = {
        conversationId,
        senderId,
        senderName,
        content,
        timestamp: Timestamp.now(),
        type: 'text'
      };

      const messageRef = await addDoc(collection(this.db, 'internalMessages'), messageData);
      console.log('[MessagingService] ✅ Message créé:', messageRef.id);

      // Mettre à jour la conversation et les compteurs de messages non lus en une seule opération
      const updateData = {
        lastMessage: content,
        lastMessageAt: Timestamp.now()
      };

      // Utiliser increment() pour une incrémentation atomique des compteurs
      // Cela évite les problèmes de race condition lors de messages rapides
      conversation.participants.forEach(participantId => {
        if (participantId !== senderId) {
          updateData[`unreadCount.${participantId}`] = increment(1);
        }
      });

      await updateDoc(conversationRef, updateData);
      console.log('[MessagingService] 📊 Compteurs incrémentés atomiquement pour les participants');


      return messageRef.id;
    } catch (error) {
      console.error('Erreur envoi message:', error);
      throw error;
    }
  }

  /**
   * Écouter les messages d'une conversation
   * @param {string} conversationId - ID de la conversation
   * @param {Function} callback - Fonction appelée à chaque mise à jour
   * @returns {Function} Fonction pour arrêter l'écoute
   */
  listenToMessages(conversationId, callback) {
    console.log('[MessagingService] 🎧 Écoute des messages pour conversation:', conversationId);
    const messagesRef = collection(this.db, 'internalMessages');
    const q = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      console.log('[MessagingService] 📨 Mise à jour messages reçue:', snapshot.docs.length, 'messages');
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      console.log('[MessagingService] 📤 Callback appelé avec', messages.length, 'messages');
      callback(messages);
    }, (error) => {
      console.error('[MessagingService] ❌ Erreur écoute messages:', error);
    });
  }

  /**
   * Récupérer les informations d'un utilisateur (nom, prénom, photo, rôle)
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} Données utilisateur
   */
  async getUserData(userId) {
    try {
      // Essayer d'abord dans employees
      let userDoc = await getDoc(doc(this.db, 'employees', userId));

      if (userDoc.exists()) {
        const data = userDoc.data();

        // Récupérer le nom du rôle depuis advanced_roles
        let roleName = 'Collaborateur';
        if (data.role) {
          try {
            const roleDoc = await getDoc(doc(this.db, 'advanced_roles', data.role));
            if (roleDoc.exists()) {
              roleName = roleDoc.data().name || data.role;
            }
          } catch (roleError) {
            console.error('Erreur récupération rôle:', roleError);
          }
        }

        return {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Utilisateur',
          photoURL: data.photoURL || data.profilePhoto || null,
          role: roleName,
          roleId: data.role,
          type: 'employee'
        };
      }

      // Sinon essayer dans clients
      userDoc = await getDoc(doc(this.db, 'clients', userId));

      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          firstName: data.firstName || data.prenom || '',
          lastName: data.lastName || data.nom || '',
          name: `${data.firstName || data.prenom || ''} ${data.lastName || data.nom || ''}`.trim() || data.name || 'Bénéficiaire',
          photoURL: data.photoURL || data.profilePhoto || data.photo || null,
          role: 'Bénéficiaire',
          type: 'client'
        };
      }

      return {
        firstName: '',
        lastName: '',
        name: 'Utilisateur inconnu',
        photoURL: null,
        role: 'Inconnu',
        type: 'unknown'
      };
    } catch (error) {
      console.error('Erreur récupération données utilisateur:', error);
      return {
        firstName: '',
        lastName: '',
        name: 'Utilisateur inconnu',
        photoURL: null,
        role: 'Inconnu',
        type: 'unknown'
      };
    }
  }

  /**
   * Écouter les conversations d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {Function} callback - Fonction appelée à chaque mise à jour
   * @returns {Function} Fonction pour arrêter l'écoute
   */
  listenToUserConversations(userId, callback) {
    const conversationsRef = collection(this.db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(q, async (snapshot) => {
      const conversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageAt: doc.data().lastMessageAt?.toDate()
      }));

      // Enrichir les conversations avec les noms des participants
      const enrichedConversations = await Promise.all(
        conversations.map(async (conversation) => {
          // Pour les groupes, garder le nom du groupe
          if (conversation.type === 'group') {
            return conversation;
          }

          // Pour les conversations 1-to-1, récupérer le nom de l'autre participant
          const otherParticipantId = conversation.participants.find(id => id !== userId);

          if (otherParticipantId) {
            const userData = await this.getUserData(otherParticipantId);
            return {
              ...conversation,
              participantNames: userData.name,
              participantData: userData
            };
          }

          return conversation;
        })
      );

      callback(enrichedConversations);
    }, (error) => {
      console.error('Erreur écoute conversations:', error);
    });
  }

  /**
   * Marquer les messages comme lus
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur
   */
  async markConversationAsRead(conversationId, userId) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount.${userId}`]: 0,
        [`lastReadTimestamp.${userId}`]: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur marquage messages lus:', error);
      throw error;
    }
  }

  /**
   * Archiver une conversation
   * @param {string} conversationId - ID de la conversation
   * @param {string} userId - ID de l'utilisateur qui archive
   */
  async archiveConversation(conversationId, userId) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        archived: true,
        archivedBy: userId,
        archivedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur archivage conversation:', error);
      throw error;
    }
  }

  /**
   * Désarchiver une conversation
   * @param {string} conversationId - ID de la conversation
   */
  async unarchiveConversation(conversationId) {
    try {
      const conversationRef = doc(this.db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        archived: false,
        archivedBy: null,
        archivedAt: null
      });
    } catch (error) {
      console.error('Erreur désarchivage conversation:', error);
      throw error;
    }
  }

  /**
   * Trouver ou créer une conversation entre deux utilisateurs
   * @param {string} user1Id - ID du premier utilisateur
   * @param {string} user2Id - ID du deuxième utilisateur
   * @param {'internal'|'client'} type - Type de conversation
   * @returns {Promise<string>} ID de la conversation
   */
  async findOrCreateConversation(user1Id, user2Id, type = 'internal') {
    try {
      // Chercher une conversation existante
      const conversationsRef = collection(this.db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', user1Id),
        where('type', '==', type)
      );

      const snapshot = await getDocs(q);
      const existingConversation = snapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(user2Id) && 
               data.participants.length === 2 && 
               !data.archived;
      });

      if (existingConversation) {
        return existingConversation.id;
      }

      // Créer une nouvelle conversation
      return await this.createConversation([user1Id, user2Id], type);
    } catch (error) {
      console.error('Erreur recherche/création conversation:', error);
      throw error;
    }
  }

  /**
   * Obtenir les statistiques de messages non lus
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<{internal: number, client: number, group: number}>} Nombre de messages non lus par type
   */
  async getUnreadCounts(userId) {
    try {
      const conversationsRef = collection(this.db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId),
        where('archived', '==', false)
      );

      const snapshot = await getDocs(q);
      let internalCount = 0;
      let clientCount = 0;
      let groupCount = 0;

      snapshot.docs.forEach(doc => {
        const conversation = doc.data();
        const unread = conversation.unreadCount?.[userId] || 0;

        if (conversation.type === 'internal') {
          internalCount += unread;
        } else if (conversation.type === 'client') {
          clientCount += unread;
        } else if (conversation.type === 'group') {
          groupCount += unread;
        }
      });

      return { internal: internalCount, client: clientCount, group: groupCount };
    } catch (error) {
      console.error('Erreur récupération compteurs non lus:', error);
      return { internal: 0, client: 0, group: 0 };
    }
  }
}

export default MessagingService;
